import React, { Fragment } from 'react';
import { Alert, Button, ModalFooter, ModalBody, Label, Input } from 'reactstrap';
import numeral from 'numeral';
import cn from 'classnames';
import { Query } from 'react-apollo';
import { gql } from 'apollo-boost';
import { FieldArray } from 'formik';
import { find } from 'lodash';
import AddButton from '~/components/addButton/addButton';
import SubmissionButton from '~/components/submissionButton/submissionButton';
import Spinner from '~/components/spinner/spinner';
import FormikInputField from '~/components/formikInputField/formikInputField';
import IconTooltip from '~/components/iconTooltip/iconTooltip';
import Effect from '~/components/formikEffect/formikEffect';
import { renderError, hasError } from '~/utils/formikValidation';

export const getProductVariants = gql`
  query productVariants($breweryId: Int!, $productId: Int) {
    productVariants(breweryId: $breweryId, productId: $productId) {
      id
      name
      packagingCost
      taxPerLitre
      vesselTypeId
      vesselType {
        id
        name
        quantity
        volume
      }
      product {
        id
        name
      }
    }
  }
`;

export const calculateWastage = (lotTransfers, productVariants, variants) => {
  const lots = lotTransfers.map(l => l.consolidatedLot || l.lot);
  const allowedVolume = lots.reduce((tot, curr) => tot + curr.currentVolume || curr.volume, 0);
  const total = variants.reduce((tot, curr) => {
    const variant = find(productVariants, v => v.id == curr.productVariantId); // eslint-disable-line eqeqeq
    if (!variant || !curr.productVariantId) {
      return tot;
    }
    return numeral(tot)
      .add(
        numeral(curr.quantity || 0)
          .multiply(
            numeral(variant.vesselType.quantity)
              .multiply(variant.vesselType.volume)
              .value()
          )
          .value()
      )
      .value();
  }, 0);
  const wastage = numeral(allowedVolume)
    .subtract(total)
    .value();
  return { total, allowedVolume, wastage };
};

const renderTotalInfo = (total, allowedVolume, wastage, costPerLitre) => {
  const totalCost = numeral(costPerLitre || 0)
    .multiply(total)
    .value()
    .toFixed(2);
  const wastageCost = numeral(costPerLitre || 0)
    .multiply(wastage)
    .value()
    .toFixed(2);
  return (
    <Fragment>
      <table className="table">
        <tbody>
          <tr>
            <td>Total</td>
            <td>
              {' '}
              <span className={cn({ error: total > allowedVolume })}>
                {total > allowedVolume ? `(${total})` : total}L
              </span>
            </td>
            <td> = ${totalCost} </td>
          </tr>
        </tbody>
        <tr>
          <td>Wastage</td>
          <td>
            <span className={cn({ error: total > allowedVolume })}>{wastage}L</span>
          </td>
          <td> = ${wastageCost} </td>
        </tr>
      </table>
    </Fragment>
  );
};

const TransferToVesselForm = ({
  onToggle,
  lotTransfers,
  onSuccess,
  brewery,
  refetchQueries,
  isSubmitting,
  product,
  values,
  errors,
  error,
  handleSubmit,
  setFieldValue
}) => {
  return (
    <Query
      query={getProductVariants}
      variables={{
        breweryId: brewery.id,
        productId: product.id
      }}
      fetchPolicy={'cache-and-network'}
    >
      {props => {
        const { loading, data } = props;
        if (loading) {
          return <Spinner className="absolute absolute-center stroke-brand-red" />;
        }

        const { total, allowedVolume, wastage } = calculateWastage(lotTransfers, data.productVariants, values.variants);
        return (
          <form onSubmit={handleSubmit}>
            <Effect
              onChange={(currentFormikState, nextFormikState) => {
                // Check each variant. If any have changed Id, set the correctponding packaging cost
                currentFormikState.values.variants.forEach((variant, idx) => {
                  try {
                    const currentVariantId = variant.productVariantId;
                    const nextVariantId = nextFormikState.values.variants[idx].productVariantId;

                    if (currentVariantId !== nextVariantId) {
                      const dataVariant = find(data.productVariants, v => v.id == nextVariantId); // eslint-disable-line eqeqeq
                      setFieldValue(`variants.${idx}.packagingCost`, dataVariant.packagingCost);
                      setFieldValue(`variants.${idx}.taxPerLitre`, dataVariant.taxPerLitre);
                    }
                  } catch (err) {
                    console.error(err);
                  }
                });
              }}
            />
            <ModalBody>
              {error && <Alert color="danger">Sorry, there was an unexpected error</Alert>}
              <p className="mb3">Do you want to confirm these vessel transfers as completed?</p>
              <div className="flex mb4 items-center">
                <div className="flex-grow-1">
                  <p className="fs">Cost of {product.name} per litre</p>
                </div>
                <div className="flex items-center">
                  <span className="pr1">$</span>
                  <FormikInputField type="number" step="1" className="w4" name="costPerLitre" />
                </div>
              </div>
              <div>
                {lotTransfers.map((l, idx) => {
                  const lot = l.consolidatedLot || l.lot;
                  return (
                    <div key={l.id} className="mb3 pb3 bb-divider flex">
                      <div className="flex-grow-1">
                        <h4 className="mv0 f5">{lot.reference}</h4>
                      </div>
                      <div>
                        <h4 className="mv0 f5">{lot.currentVolume || lot.volume}L</h4>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="bb-divider pb3 mb3">
                <div className="mb3 ml4">
                  <FormikInputField
                    name="trackFinishedVariants"
                    checked={values.trackFinishedVariants}
                    type="checkbox"
                  />
                  <Label>Track finished goods in Xero</Label>
                </div>
                {values.trackFinishedVariants && (
                  <FieldArray
                    name="variants"
                    validateOnChange={false}
                    render={arrayHelpers => {
                      return (
                        <div>
                          <Label>Which products?</Label>
                          {values.variants.map((variant, idx) => {
                            let lineTotal = 0;
                            const productVariant = find(product.variants, v => v.id == variant.productVariantId); // eslint-disable-line eqeqeq
                            if (productVariant) {
                              const variantVolume = numeral(variant.quantity || 0)
                                .multiply(productVariant.vesselType.quantity)
                                .multiply(productVariant.vesselType.volume)
                                .value();
                              const totalCostPerLitre = numeral(values.costPerLitre || 0)
                                .add(variant.taxPerLitre || 0)
                                .value();
                              const totalPackagingCost = numeral(variant.quantity || 0)
                                .multiply(variant.packagingCost || 0)
                                .value();
                              const totalLineVolumeCost = numeral(totalCostPerLitre)
                                .multiply(variantVolume)
                                .value();
                              lineTotal = numeral(totalPackagingCost)
                                .add(totalLineVolumeCost)
                                .value()
                                .toFixed(2);
                            }

                            console.log('line total', lineTotal);
                            return (
                              <Fragment>
                                <div className="flex mb3">
                                  <div className="flex-grow-1 flex justify-end">
                                    <div className="flex-grow-1 flex">
                                      <FormikInputField
                                        type="number"
                                        step="1"
                                        className="w3"
                                        name={`variants.${idx}.quantity`}
                                        invalid={hasError(errors, idx, 'variants', 'quantity')}
                                      />
                                      <span className="dib mh2 self-center">x</span>
                                      <FormikInputField
                                        className="flex-grow-1"
                                        type="select"
                                        name={`variants.${idx}.productVariantId`}
                                        invalid={hasError(errors, idx, 'variants', 'productVariantId')}
                                      >
                                        <option>Select a product variant</option>
                                        {data.productVariants.map(pv => (
                                          <option value={pv.id}>{pv.name}</option>
                                        ))}
                                      </FormikInputField>
                                    </div>
                                  </div>
                                  <div className="flex items-center">
                                    <span className="ph1"> @ $</span>
                                    <FormikInputField
                                      type="number"
                                      step="0.01"
                                      className="w3-5"
                                      name={`variants.${idx}.taxPerLitre`}
                                      invalid={hasError(errors, idx, 'variants', 'taxPerLitre')}
                                    />
                                  </div>
                                  <div className="flex items-center">
                                    <span className="ph1">+ </span>
                                    <FormikInputField
                                      type="number"
                                      step="0.01"
                                      className="w3-5"
                                      name={`variants.${idx}.packagingCost`}
                                      invalid={hasError(errors, idx, 'variants', 'packagingCost')}
                                    />
                                  </div>
                                  <div className="flex items-center">
                                    <span className="ph1"> = </span>
                                    <Input disabled type="number" step="0.01" className="w3-5" value={lineTotal} />
                                  </div>
                                  {values.variants.length > 1 && (
                                    <div className="flex self-center">
                                      <IconTooltip
                                        icon="delete-circle"
                                        text="Remove"
                                        className="ml3 stroke-brand-red"
                                        onClick={() => arrayHelpers.remove(idx)}
                                      />
                                    </div>
                                  )}
                                </div>
                                <div>
                                  {renderError(errors, idx, 'variants', 'quantity')}
                                  {renderError(errors, idx, 'variants', 'productVariantId')}
                                </div>
                              </Fragment>
                            );
                          })}
                          <AddButton
                            color="primary"
                            onClick={() =>
                              arrayHelpers.push({
                                quantity: 0,
                                packagingCost: 0,
                                taxPerLitre: 0
                              })
                            }
                          >
                            Add another
                          </AddButton>
                        </div>
                      );
                    }}
                  />
                )}
              </div>
              {values.trackFinishedVariants && (
                <div className="flex">
                  <div className="flex-grow-1" />
                  <div>{renderTotalInfo(total, allowedVolume, wastage, values.costPerLitre)}</div>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={onToggle}>
                Cancel
              </Button>{' '}
              <SubmissionButton
                color="primary"
                onClick={() => {
                  setFieldValue('wastage', wastage);
                }}
                isSubmitting={isSubmitting}
              >
                Transfer
              </SubmissionButton>
            </ModalFooter>
          </form>
        );
      }}
    </Query>
  );
};

export default TransferToVesselForm;



// WEBPACK FOOTER //
// ./src/containers/confirmTransferToVesselModal/transferToVesselForm.jsx